import * as React from 'react'
import Page from '../../components/page/Page'
import {
  createStyles,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  Paper,
  Theme,
  Typography,
  withStyles,
  WithStyles,
  withTheme,
  WithTheme
} from "@material-ui/core";
import {RouteComponentProps} from "@reach/router";
import MarginRow from "../../components/page/MarginRow";
import DialogButton from "../../components/form/DialogButton";
import {observable} from "mobx";
import {inject, observer} from "mobx-react";
import FormValidator from "../../components/form/FormValidator";
import TextFieldValidator from "../../components/form/TextFieldValidator";
import ControlTower, {Routes} from "../../components/ControlTower";
import Notify from "../../components/notify/Notify";
import UserStore, {UserStoreConstants} from "../../stores/UserStore";
import User from "../../model/User";
import {ActivityType, UpdateUserInput, UserRole, UserStatus} from "../../API";
import Visible from "../../components/Visible";
import AccountStore from "../../stores/AccountStore";
import ProgressButton from "../../components/form/ProgressButton";
import {getErrorMessage} from "../../stores/StoreUtilities";
import SignInHero from "../../images/sign-in-hero.jpg";
import DialogTitle from "@material-ui/core/DialogTitle";
import Tracking from "../../components/Tracking";

const styles = (theme: Theme) => createStyles({
  rootStyle: {
    flexGrow: 1,
    justifyContent: 'top',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh'
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    justifyContent: 'top',
    alignItems: 'center',
  },
  dialogHero: {
    width: "auto",
    height: '250px',
    paddingTop: 190,
    backgroundImage: `url(${SignInHero})`,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center"
  },
  titleBackground: {
    width: "100%",
  },
  appTitle: {
    paddingTop: 7,
    color: theme.palette.primary.contrastText,
    fontSize: 28,
    fontWeight: 800,
    textAlign: "center",
    fontFamily: [
      'Archivo Black',
      'sans-serif'
    ].join(','),
  },
  message: {
    color: theme.palette.error.main,
    fontSize: 18,
    marginTop: 10
  },
  instructions: {
    color: theme.palette.text.primary,
    fontSize: 18,
    marginTop: 10
  },
  dialogPaper: {
    maxWidth: 480,
    [theme.breakpoints.down('sm')]: {
      marginTop: 'calc(50vh - 375px)',
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: 'calc(50vh - 350px)',
    },
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  dialogContent: {
    padding: theme.spacing(1),
    "&:first-child": {
      paddingTop: 4
    }
  },
  dialogActions: {
    justifyContent: "center",
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  dialogTitle: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    textAlign: 'center'
  },
  label: {
    color: theme.palette.text.secondary,
    marginLeft: 0,
  },
  msgLink: {
    color: theme.palette.secondary.main
  },
  errorLink: {
    color: "white"
  }
})

interface IRegisterPageProps {
  userStore?: UserStore
  accountStore?: AccountStore
  notify?: Notify
  accountId?: string
  userId?: string
}

@inject("userStore", "accountStore", "notify" /*, "privacyPolicy", "termsOfService" */)
@observer
class RegisterPage extends React.Component<WithStyles<typeof styles> & RouteComponentProps & IRegisterPageProps & WithTheme> {

  @observable values = {
    email: "",
    password: "",
    phone: "",
    userId: "",
    accountId: "",
    role: UserRole.Student,
    verificationCode: ""
  }
  @observable user?: User
  @observable isVerifying: boolean = false
  @observable autoVerify: boolean = false
  @observable isProcessing: boolean = false
  @observable isValid: boolean = false
  @observable message = ""

  componentDidMount () {
    this.loadUser()
  }

  loadUser = async () => {
    const { accountId, userId, userStore } = this.props

    // Handle old registration links with accountId and userId
    const userName = (accountId && !userId) ? accountId : userId

    if (userName) {
      const user = await userStore!.getPublicUser(userName)
        .catch((err: Error) => {
          this.message = getErrorMessage(err)
        })
      if (user) {
        this.values.userId = userName
        this.values.email = user.email
        this.values.accountId = user.accountId
        this.values.role = user.role
        if (user.userStatus === UserStatus.Invited) {
          this.isValid = true
        } else {
          this.message = `This email is currently ${user.userStatus.toLowerCase()}`
        }
      } else {
        this.message = `Registration not found`
      }
    } else {
      this.message = "Invalid registration link"
    }
  }

  render() {
    const { classes } = this.props

    const title = "Register"

    return (
      <Page title={title}>
        <MarginRow>
          <Grid container className={classes.root} direction="column">
            <Paper className={classes.dialogPaper}>
              <Grid container direction="row" className={classes.dialogHero}>
                <Grid item className={classes.titleBackground}>
                  <div className={classes.appTitle}>
                    Hazmat & Rescue
                  </div>
                </Grid>
              </Grid>
              <FormValidator onSubmit={this.onSubmit} autoComplete="off" name="signupForm" id="signupForm">
                <DialogTitle
                  id="login-dialog-title"
                  className={ classes.dialogTitle }
                >
                  Registration
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                  <Visible if={!this.isVerifying}>
                    <DialogContentText className={classes.instructions}>
                      Please confirm your registered email address and create a password to complete your registration.
                    </DialogContentText>
                    <DialogContentText className={classes.instructions}>
                      <span>If you have already registered, <a href="/" className={classes.msgLink}>click to Sign In.</a></span>
                    </DialogContentText>
                    <TextFieldValidator
                      margin="dense"
                      name="email"
                      id="EmailInput"
                      label="Email"
                      variant="standard"
                      placeholder=""
                      type="text"
                      validators={{required:true}}
                      value={this.values.email}
                      onChange={this.onChange}
                      fullWidth
                      autoCorrect="off"
                      autoCapitalize="off"
                      autoComplete="on"
                      disabled={true}
                    />
                    <TextFieldValidator
                      margin="dense"
                      name="password"
                      label="Create Password"
                      type="password"
                      variant="standard"
                      validators={{required:true, isStrongPassword:3}}
                      value={this.values.password}
                      onChange={this.onChange}
                      fullWidth
                    />
                  </Visible>

                  <DialogContentText className={classes.message}>
                    {this.message}
                  </DialogContentText>

                  <Visible if={this.isVerifying}>
                    <Grid item className={classes.message}>
                      <Typography variant="body2" className={classes.instructions}>
                        Please check your email for a verification code.
                      </Typography>
                    </Grid>
                    <TextFieldValidator
                      margin="dense"
                      name="verificationCode"
                      label="Verification Code"
                      type="text"
                      variant="standard"
                      value={this.values.verificationCode}
                      validators={{required:true, matches:"^\\d{6}$"}}
                      onChange={this.onChange}
                      fullWidth
                    />
                    <DialogButton variant="tertiary" onClick={this.onResendCode}>
                      Resend verification code
                    </DialogButton>
                  </Visible>

                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                  <DialogButton variant="secondary" onClick={this.onCancel}>
                    CANCEL
                  </DialogButton>
                  <ProgressButton variant="contained" color="secondary" fullWidth={false}
                                  type="submit" processing={this.isProcessing} disabled={!this.isValid || this.isProcessing}>
                    { this.isVerifying ? "VERIFY" : "REGISTER" }
                  </ProgressButton>
                </DialogActions>
              </FormValidator>
            </Paper>
          </Grid>
        </MarginRow>
      </Page>
    )
  }

  onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name
    this.values[name] = event.target.value
  }

  onCancel = () => {
    ControlTower.back()
  }

  onSubmit = async () => {
    const {userStore, accountStore, notify, classes} = this.props
    const values = this.values
    let verification = ""
    this.message = ""

    if (!this.isVerifying) {
      this.isProcessing = true

      // Sign-up user
      const signupResult = await userStore!.signUp(values.userId, values.password, values.email, values.phone,
                                                   values.accountId, values.role)
        .catch(async (err: Error) => {
          let message
          if (err.name === UserStoreConstants.USERNAME_EXISTS_EXCEPTION) {
            // Try resending confirmation code
            await userStore!.resendSignUp(this.values.userId)
              .then((result: any) => {
                this.isVerifying = true
              })
              .catch((err: Error) => {
                if (err.message === UserStoreConstants.USER_ALREADY_CONFIRMED_MESSAGE) {
                  message = <span>{err.message} <a href="/" className={classes.errorLink}>Click here to Sign In.</a></span>
                } else {
                  message = err.message
                }
              })
          } else {
            message = `Unable to create user. ${err.message}`
          }
          if (message) {
            notify!.show("error", message)
            Tracking.event({action: 'RegisterError', label: message})
          }
          this.isProcessing = false
        })
      if (signupResult) {
        if (this.values.role === UserRole.Student) {
          // Auto verify
          verification = "AUTO"
        } else {
          this.isVerifying = true
          verification = "PENDING"
        }
      }
      this.isProcessing = false
    }

    if ((this.isVerifying && verification !== "PENDING") || verification === "AUTO") {
      this.isProcessing = true

      if (verification !== "AUTO") {
        verification = await userStore!.confirmSignUp(values.userId, values.verificationCode)
          .catch((err: Error) => {
            notify!.show("error", "Unable to verify account")
            Tracking.event({action: 'RegisterVerifyError', label: err.message})
            this.isProcessing = false
          })
      }

      if (verification === "SUCCESS" || verification === "AUTO") {
        // Sign in
        let user = await userStore!.signIn(values.userId, values.password)
          .catch((err: Error) => {
            if (err.message !== UserStoreConstants.USER_NOT_FOUND) {
              notify!.show("error", "Unable to signin")
              this.isProcessing = false
            }
          })

        if (user) {
          // Update User to registered
          const updateUserInput: UpdateUserInput = {
            id: user.id,
            userStatus: UserStatus.Registered
          }

          user = await userStore!.updateUser(updateUserInput)
            .catch((err: Error) => {
              notify!.show("error", "Unable to update user")
              Tracking.event({action: 'RegisterError', label: `Unable to update user: ${err.message}`})
              this.isProcessing = false
            })

          if (user.isAdmin) {
            // Sign-in again to authenticate with new role
            user = await userStore!.signIn(values.userId, values.password)
          }

          // Initialize accountStore for new user
          if (user.account) {
            await accountStore!.init(user.account)
          } else {
            console.log("user.account is undefined!")
          }

          this.isProcessing = false
          notify!.show("success", "Welcome to Hazmat & Rescue!")
          Tracking.event({action: 'RegisterSuccess'})
          userStore!.createActivity(ActivityType.UserRegister, user.id)
          if (user.role === UserRole.Owner) {
            ControlTower.route(`${Routes.account}/${user.accountId}/classes`)
          } else if (user.role === UserRole.Admin) {
            ControlTower.route(Routes.accounts)
          } else {
            ControlTower.route(Routes.myClasses)
          }
        }
      }
    }
  }

  onResendCode = () => {
    const { userStore, notify} = this.props
    Tracking.event({action: 'RegisterResendCode'})
    userStore!.resendSignUp(this.values.userId)
      .then((result: any) => {
        notify!.show("success", "Verification code resent")
      })
      .catch((err: Error) => {
        notify!.show("error", "Unable to resend verification code")
      })
  }

}

export default withTheme((withStyles(styles)(RegisterPage)))